import api from 'services/api';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, unknownValue, withDefault } from 'validation';

import {
  outputMatcherSchema,
  parameterSchema,
  raiseEventSchema,
  retentionPolicySchema,
  waitEventSchema,
  type OutputMatcher,
  type Parameter,
  type RaiseEvent,
  type RetentionPolicy,
  type WaitEvent,
} from './types';

// GET /node_definitions/:id — the single-record read (full detail). Source of truth for the wire shape: nap-backend
//   modules/generic_functions/node_definitions/getNodeDefinitionByZoneAndName.js — the SELECT + JSON_AGG subqueries.
//   modules/generic_functions/node_definitions/getNodeDefinition.js              — id -> zone_name/name resolver, delegates to the above.
//   routes/node_definitions.js GET handlers                                      — post-fetch enrichment: attach `version`,
//     derive `runtime_limit_*_hhmmss` from the ms columns, replace `folder_id` with the resolved `folder_name` PATH, and
//     refresh `locks` to a fresh array of "zone|lock" tag strings.
// Update there first if the wire changes, then mirror here. Validated wire data is immutable, so the types are deeply readonly.
//
// This envelope is redeclared per endpoint (GET-one here, Created/Updated on POST/PUT): each route handler assembles the
// record itself, so they are equal-by-coincidence, not identity. The sub-objects come from ./types (shared by identity).
//
// The flat, form-owned fields are strictly typed and validated. Four members are deliberately OPAQUE: `config` (polymorphic
// per node type), `handles`, `child_nodes`, and `child_edges` (the Flow graph). They are round-tripped unchanged; each is
// narrowed to a strict shape only where its own editor needs to read into it. `unknown` — never `any` — keeps that boundary honest.
export interface NodeDefinition {
  readonly node_definition_id: number;
  // Optimistic-concurrency token (attached by the route). Echoed back as `if_match_version` on update.
  readonly version: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly sub_type: string | null;
  readonly documentation: string | null;
  readonly zone_id: number;
  readonly zone_name: string;
  // Full slash-joined folder PATH (the route rewrites folder_id -> path); `folder_id` is never on the wire.
  readonly folder_name: string | null;
  // Computed "queueZone|queueName" string (or null); the raw id columns are dropped by the route.
  readonly default_queue_path: string | null;
  readonly restrict_to_scheduling_window: number | null;
  readonly auto_restart_enabled: boolean;
  readonly auto_restart_amount: number;
  readonly auto_restart_delay_seconds: number;
  // Return-code filter for auto-restart: an ordered, de-duplicated list of exit codes (negatives allowed). Empty = restart
  // on any failure; non-empty = restart only when the failed instance's return code is one of these. Backend column is
  // `integer[] NOT NULL DEFAULT '{}'`, so it's always present on a current backend; defaulted to [] for pre-#1380 backends.
  readonly restart_on_return_codes: readonly number[];
  readonly hold_on_submit: boolean;
  // Raw millisecond columns.
  readonly runtime_limit_min: number | null;
  readonly runtime_limit_max: number | null;
  // Route-derived HH:MM:SS (or a variable expression) — present only when a value exists.
  readonly runtime_limit_min_hhmmss?: string;
  readonly runtime_limit_max_hhmmss?: string;
  readonly runtime_limit_event: number | null;
  readonly runtime_limit_terminate: boolean;
  // Read-only capability flag from node_type_configs; not accepted on write.
  readonly editable_handles: boolean;
  // "zone|lock" tag strings.
  readonly locks: readonly string[];
  readonly parameters: readonly Parameter[];
  readonly wait_events: readonly WaitEvent[];
  readonly raise_events: readonly RaiseEvent[];
  readonly output_matchers: readonly OutputMatcher[];
  readonly retention_policies: readonly RetentionPolicy[];
  // Opaque / bridge-owned (see the note above): round-tripped unchanged, tightened when their editors migrate.
  readonly config: unknown;
  readonly handles: readonly unknown[];
  readonly child_nodes: readonly unknown[];
  readonly child_edges: readonly unknown[];
}

const nodeDefinitionSchema = object<NodeDefinition>({
  node_definition_id: num(),
  version: num(),
  name: str(),
  description: nullable(str()),
  type: str(),
  sub_type: nullable(str()),
  documentation: nullable(str()),
  zone_id: num(),
  zone_name: str(),
  folder_name: nullable(str()),
  default_queue_path: nullable(str()),
  restrict_to_scheduling_window: nullable(num()),
  auto_restart_enabled: bool(),
  auto_restart_amount: num(),
  auto_restart_delay_seconds: num(),
  restart_on_return_codes: withDefault(array(num()), []),
  hold_on_submit: bool(),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  runtime_limit_min_hhmmss: optional(str()),
  runtime_limit_max_hhmmss: optional(str()),
  runtime_limit_event: nullable(num()),
  runtime_limit_terminate: bool(),
  editable_handles: bool(),
  locks: withDefault(array(str()), []),
  parameters: withDefault(array(parameterSchema), []),
  wait_events: withDefault(array(waitEventSchema), []),
  raise_events: withDefault(array(raiseEventSchema), []),
  // Absent key (definition with no matchers, or a node type that doesn't support them) -> [].
  output_matchers: withDefault(array(outputMatcherSchema), []),
  retention_policies: withDefault(array(retentionPolicySchema), []),
  config: unknownValue(),
  handles: withDefault(array(unknownValue()), []),
  child_nodes: withDefault(array(unknownValue()), []),
  child_edges: withDefault(array(unknownValue()), []),
});

/**
 * Parse a single-record node-definition response. Throws on structural failure or drift of a critical field (identity + the
 * display/save load-bearing `name` / `type` / `zone_name`); non-critical drift logs a warning and substitutes a default.
 * `version` is intentionally NOT enforced: a response missing it would otherwise turn a successful save into a spurious
 * error; it defaults to 0 and a stale token surfaces as a 409 on the next save instead.
 */
export const parseNodeDefinition = (raw: unknown, source: string): NodeDefinition => {
  const result = runSchema(nodeDefinitionSchema, raw, source);
  if (!result.ok) {
    throw new Error(`${source}: node definition response failed validation`);
  }
  return enforce(result.value, result.warnings, ['node_definition_id', 'name', 'type', 'zone_name']);
};

export const getNodeDefinition = async (id: number): Promise<NodeDefinition> => {
  const res = await api.get(`/node_definitions/${id}`);
  return parseNodeDefinition(res.data, 'getNodeDefinition');
};
